import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import type { CardProperties } from './types';
import { ArticleCardVariants, CardTypes, FolderCardVariants } from './types';

export interface FcpCardAction {
    key: string;
    label: string;
}

@Component({
    selector: 'fcp-card',
    templateUrl: './fcp-card.component.html',
    styleUrls: ['./fcp-card.component.less'],
    standalone: false
})
export class FcpCardComponent implements OnInit, AfterViewInit {
    @Input() icon: string | string[] = '';
    @Input() variant = '';
    @Input() coverImg = '';
    @Input() imgLabel = '';
    @Input() imageList: string[] = [];
    @Input() actionsList: FcpCardAction[] = [];
    @Input() cardType: CardTypes = CardTypes.ARTICLE;
    @Input() payload: any = {};
    @Input() hoverMode: 'none' | 'light' | 'dark' = 'none';

    @Output() outputActions: EventEmitter<any> = new EventEmitter<any>();
    @Output() outputClick: EventEmitter<any> = new EventEmitter<any>();

    public cardProperties: CardProperties;

    public isError = true;
    public label = '';

    @ViewChild('cardLabel')
    cardLabel: ElementRef<HTMLDivElement>;

    ngOnInit() {
        if (this.cardType === CardTypes.FOLDER) {
            this.validateFolderCard();
        }

        if (this.cardType === CardTypes.ARTICLE) {
            this.validateArticleCard();
        }

        this.isError = false;

        this.cardProperties = {
            label: this.label,
            icon: this.icon,
            variant: this.variant,
            coverImg: this.coverImg,
            imgLabel: this.imgLabel,
            imageList: this.imageList,
            actionsList: this.actionsList,
            payload: this.payload,
            hoverMode: this.hoverMode
        };
    }

    ngAfterViewInit() {
        this.cardProperties = {
            ...this.cardProperties,
            label: this.cardLabel?.nativeElement?.outerText
        };
        this.cardLabel?.nativeElement?.remove();
    }

    callAction(data: string): void {
        this.outputActions.next(data);
    }

    onClick(data): void {
        this.outputClick.next(data);
    }

    validateFolderCard(): void {
        const varients: string[] = Object.values(FolderCardVariants);
        if (!varients.includes(this.variant)) {
            throw Error(`Invalid variant: ${this.variant}. Valid variants are: ${varients.join(', ')}`);
        }

        if (
            this.variant === FolderCardVariants.CREATE &&
            (this.imageList.length !== 0 || this.actionsList.length !== 0)
        ) {
            throw Error(`card-create need be used without images or actions`);
        }

        if (
            (this.variant === FolderCardVariants.COVER || this.variant === FolderCardVariants.EMPTY_COVER) &&
            !this.coverImg
        ) {
            throw Error(`card-cover / card-empty-cover variant requires coverImg to be set`);
        }

        if (this.variant === FolderCardVariants.CREATE && !this.icon) {
            throw Error(`card-create variant requires icon to be set`);
        }

        if (this.variant === FolderCardVariants.NO_COVER && this.imageList.length === 0) {
            throw Error(`card-no-cover variant requires images list to be set`);
        }
    }

    validateArticleCard(): void {
        const varients: string[] = Object.values(ArticleCardVariants);
        if (!varients.includes(this.variant)) {
            throw Error(`Invalid variant: ${this.variant}. Valid variants are: ${varients.join(', ')}`);
        }

        if (
            this.variant === ArticleCardVariants.MM_CREATE &&
            (this.imageList.length !== 0 || this.actionsList.length !== 0)
        ) {
            throw Error(`card-create need be used without images or actions`);
        }
    }

    getCardType(type: keyof typeof CardTypes): string {
        return CardTypes[type];
    }
}
